import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_BASE } from '../lib/config';

// API base is provided by shared config (CRA/Next compatible)
const API = `${API_BASE}`;

const GoogleAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    handleGoogleCallback();
  }, []);

  const handleGoogleCallback = async () => {
    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (error) {
        throw new Error(`Google OAuth error: ${error}`);
      }

      if (!code) {
        throw new Error('認証コードが見つかりません');
      }

      // Send code to backend using URLSearchParams for robust x-www-form-urlencoded handling
      const params = new URLSearchParams();
      params.append('code', code);
      params.append('state', state || '');
      params.append('front_url', window.location.origin);

      const response = await axios.post(`${API}/auth/google/callback`, params);

      const { access_token, user } = response.data;

      // Store token and user data
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      updateUser(user);

      toast.success('Googleアカウントでログインしました！');

      // Redirect based on user type
      if (user.user_type === 'employer') {
        navigate('/business/dashboard');
      } else if (user.user_type === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/mypage');
      }
    } catch (error) {
      console.error('Google OAuth callback error:', error);
      const detail = error.response?.data?.detail || error.message || 'ログインに失敗しました';
      setError(detail);
      toast.error(`Googleログイン失敗: ${typeof detail === 'object' ? JSON.stringify(detail) : detail}`);

      // Redirect to login page after a delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Googleアカウントでログイン中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">ログインエラー</h3>
          <p className="mt-1 text-sm text-gray-500">{typeof error === 'object' ? JSON.stringify(error) : error}</p>
          <p className="mt-2 text-xs text-gray-400">3秒後にログインページに戻ります...</p>
        </div>
      </div>
    );
  }

  return null;
};

const LineAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    handleLineCallback();
  }, []);

  const handleLineCallback = async () => {
    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (error) {
        throw new Error(`LINE OAuth error: ${error}`);
      }

      if (!code) {
        throw new Error('認証コードが見つかりません');
      }

      // Send code to backend using URLSearchParams
      const params = new URLSearchParams();
      params.append('code', code);
      params.append('state', state || '');
      params.append('front_url', window.location.origin);

      const response = await axios.post(`${API}/auth/line/callback`, params);

      const { access_token, user } = response.data;

      // Store token and user data
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      updateUser(user);

      toast.success('LINEアカウントでログインしました！');

      // Redirect based on user type
      if (user.user_type === 'employer') {
        navigate('/business/dashboard');
      } else if (user.user_type === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/mypage');
      }
    } catch (error) {
      console.error('LINE OAuth callback error:', error);
      const detail = error.response?.data?.detail || error.message || 'ログインに失敗しました';
      setError(detail);
      toast.error(`LINEログイン失敗: ${typeof detail === 'object' ? JSON.stringify(detail) : detail}`);

      // Redirect to login page after a delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">LINEアカウントでログイン中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">ログインエラー</h3>
          <p className="mt-1 text-sm text-gray-500">{typeof error === 'object' ? JSON.stringify(error) : error}</p>
          <p className="mt-2 text-xs text-gray-400">3秒後にログインページに戻ります...</p>
        </div>
      </div>
    );
  }

  return null;
};

export { GoogleAuthCallback, LineAuthCallback };